import React from 'react';
import { motion } from 'framer-motion';
import { 
  FaWind, 
  FaTint, 
  FaEye, 
  FaThermometerHalf,
  FaCompass,
  FaSpinner,
  FaStar
} from 'react-icons/fa';

const WeatherCard = ({ weather, loading, error, locationName, onToggleFavorite, isFavorite }) => {
  if (loading) {
    return (
      <motion.div 
        className="glass-card p-8"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="text-4xl text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading weather data...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div 
        className="glass-card p-8"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="text-center text-red-600">
          <p className="text-lg font-medium">Error loading weather data</p>
          <p className="text-sm mt-2">{error}</p>
        </div>
      </motion.div>
    );
  }

  if (!weather) return null;

  const weatherMetrics = [
    {
      icon: <FaWind className="text-lg" />,
      label: 'Wind Speed',
      value: `${weather.windSpeed} mph`
    },
    {
      icon: <FaTint className="text-lg" />,
      label: 'Humidity',
      value: `${weather.humidity}%`
    },
    {
      icon: <FaEye className="text-lg" />,
      label: 'Visibility',
      value: `${weather.visibility} km`
    },
    {
      icon: <FaThermometerHalf className="text-lg" />,
      label: 'Pressure',
      value: `${weather.pressure} hPa`
    }
  ];

  return (
    <motion.div 
      className="glass-card p-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      {/* Location and Temperature */}
      <div className="flex justify-between items-start mb-6">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h2 className="text-3xl font-bold text-gray-800">
              {locationName || 'San Mateo'}
            </h2>
            {onToggleFavorite && (
              <motion.button
                onClick={onToggleFavorite}
                className={`p-2 rounded-xl transition-all duration-200 ${
                  isFavorite 
                    ? 'bg-yellow-500/20 text-yellow-600' 
                    : 'bg-white/20 text-gray-400 hover:text-yellow-500 hover:bg-yellow-500/10'
                }`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaStar className={`text-lg ${isFavorite ? 'fill-current' : ''}`} />
              </motion.button>
            )}
          </div>
          <p className="text-gray-600">
            {locationName ? 'Selected Location' : 'California, United States'}
          </p>
        </div>
        <div className="text-right">
          <div className="text-6xl font-bold text-gray-800 mb-1">
            {weather.temperature}°F
          </div>
          <p className="text-gray-600">Feels like {weather.feelsLike}°F</p>
        </div>
      </div>

      {/* Weather Condition */}
      <div className="flex items-center space-x-4 mb-6">
        <div className="text-4xl">{weather.icon}</div>
        <div>
          <h3 className="text-xl font-semibold text-gray-800">{weather.condition}</h3>
          <p className="text-gray-600">{weather.time}</p>
        </div>
      </div>

      {/* Weather Metrics Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {weatherMetrics.map((metric, index) => (
          <motion.div
            key={metric.label}
            className="glass-card p-4 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
          >
            <div className="flex items-center justify-center mb-2 text-blue-600">
              {metric.icon}
            </div>
            <p className="text-sm text-gray-600 mb-1">{metric.label}</p>
            <p className="text-lg font-semibold text-gray-800">{metric.value}</p>
          </motion.div>
        ))}
      </div>

      {/* Wind Direction */}
      <motion.div 
        className="glass-card p-4 flex items-center space-x-3"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.7 }}
      >
        <FaCompass 
          className="text-lg text-blue-600" 
          style={{ transform: `rotate(${weather.windDirection}deg)` }}
        />
        <span className="text-gray-800">
          Wind from {weather.windDirectionText} ({weather.windDirection}°)
        </span>
      </motion.div>
    </motion.div>
  );
};

export default WeatherCard;
